import { utmCta } from "./shared-ctas";
import type { PageCopy } from "./page-copy-types";

// ─── /ai-voice-receptionist (consolidated SEO/AEO canonical) ───────────
// Merges /ai-voice (hub) and /ai-receptionist (pillar) into one canonical
// page targeting "AI voice receptionist", "AI receptionist", and "AI voice
// agent" query families. Both old URLs 301 → here.
export const AI_VOICE_RECEPTIONIST_COPY: PageCopy = {
  eyebrow: "AI Voice Receptionist",
  h1: "Your AI Voice Receptionist",
  lede: "Hear the difference. Talk to Bri right now — she answers calls, captures leads, and books appointments 24/7.",
  ctaPrimary: utmCta({
    label: "Get started",
    source: "organic",
    medium: "landing",
    campaign: "ai-voice-receptionist",
  }),
  // canonical-divergence: managed/enterprise setup offer routes to the
  // Enterprise contact path (not the hidden plan selector).
  ctaSecondary: { label: "Get Custom Setup", href: "/enterprise-pricing" },
  trustSignals: [
    "$99/mo AI Receptionist",
    "30-day money-back guarantee",
    "Cancel anytime",
    "Custom setup available",
  ],
  sections: [
    {
      id: "what-is",
      eyebrow: "Definition",
      heading: "What Is an AI Voice Receptionist?",
      body: "An AI voice receptionist is software that can answer inbound business calls, collect caller information, follow configured intake questions, request appointments, and route or escalate calls. BizRnR supports configurable 24/7 coverage while keeping the business in control of scripts, rules, connected services, and human fallback.",
    },
    {
      id: "problem",
      heading: "Missed calls interrupt the customer journey",
      body: "When a business cannot answer, a caller may leave a message, try another channel, or contact another provider. The financial effect varies by business and should be measured from its own call and conversion records. BizRnR focuses on consistent coverage, structured intake, appointment requests, and clear escalation rather than an invented universal loss figure.",
      bullets: [
        "Coverage gap. Callers can arrive while staff are busy or the office is closed.",
        "Intake consistency. A configured workflow asks the same approved questions and records the answers.",
        "Human fallback. Escalation rules define who is notified or receives a transfer when judgment is required.",
      ],
    },
    {
      id: "how-it-works",
      eyebrow: "How it works",
      heading: "How BizRnR's AI Voice Receptionist Works",
      body: "A business forwards calls or uses a provisioned number, then configures Bri's greeting, intake questions, appointment workflow, and escalation rules. BizRnR can collect approved caller details, create an interaction record, request supported calendar appointments, and notify or transfer to a person when the configured workflow requires it. Businesses should test representative calls and fallback paths before launch.",
    },
    {
      id: "comparison",
      heading:
        "AI Voice Receptionist vs Human Receptionist: What's the Difference?",
      body: "An AI receptionist applies a configured workflow consistently and can provide around-the-clock coverage without scheduling a front-desk shift. A human receptionist can exercise judgment in sensitive or unfamiliar situations. BizRnR starts at a published $99 per month and supports human escalation; buyers should compare current prices, billing units, workflow limits, integrations, and the calls that require a person.",
    },
    {
      id: "industries",
      heading: "AI Voice Receptionist for Every Small Business",
      body: "BizRnR's AI voice receptionist works for 20+ small business industries out of the box, including HVAC, dental, real estate, auto repair, restaurants, legal services, landscaping, and property management. Each industry template comes pre-configured with vertical-specific call flows, qualification questions, and terminology so your AI receptionist handles callers like a trained specialist from day one.",
    },
    {
      id: "pricing",
      heading: "How Much Does an AI Voice Receptionist Cost?",
      body: "An AI voice receptionist from BizRnR starts at $99/month, cancel-anytime billing, and a 30-day money-back guarantee. The AI Receptionist plan includes 100 interactions for self-serve solopreneurs, while higher-volume teams can talk to us about Enterprise, managed setup, and expansion paths.",
    },
    {
      id: "faq",
      heading: "Frequently Asked Questions About AI Voice Receptionists",
    },
    {
      id: "final-cta",
      heading: "Your AI Voice Receptionist. Ready When You Are.",
      body: "Start AI Receptionist for $99/mo. Or let our team configure everything — custom scripts, your hours, your industry. No technical work on your end.",
      reassurance: "No long-term contracts. Cancel anytime.",
    },
  ],
  faqs: [
    {
      q: "What is an AI voice receptionist?",
      a: "An AI voice receptionist is software that can answer inbound business calls, collect caller information, follow configured intake questions, request appointments, and route or escalate calls. AI receptionist, AI voice agent, and AI voice receptionist are overlapping terms; actual capabilities and the need for a human depend on the provider and workflow.",
    },
    {
      q: "How does an AI voice receptionist work?",
      a: "A business forwards calls or uses a provisioned number, then configures the greeting, approved questions, appointment workflow, and escalation rules. When a call arrives, the AI follows that configuration, collects caller information, creates an interaction record, and notifies or transfers to a person when required.",
    },
    {
      q: "Can an AI voice receptionist book appointments?",
      a: "Yes — BizRnR's AI voice receptionist books appointments directly into your Google Calendar, Microsoft, or iCal, and sends a confirmation SMS to the caller automatically. You receive a real-time notification for every booked appointment, so nothing falls through the cracks.",
    },
    {
      q: "How much does an AI voice receptionist cost?",
      a: "BizRnR's AI Receptionist plan starts at $99 per month with a published 30-day money-back guarantee. Other providers use per-call, per-minute, tiered, or custom pricing. Compare current provider pricing and expected volume rather than relying on a universal savings percentage.",
    },
    {
      q: "What's the difference between an AI voice receptionist and an answering service?",
      a: "An AI voice receptionist uses a configured voice workflow; a traditional answering service generally uses human agents and may bill by call or minute. BizRnR publishes a $99 monthly starting price and supports structured intake, interaction records, appointment workflows, and human escalation. Compare each provider's current documentation and billing terms.",
    },
    {
      q: "Which businesses benefit most from an AI voice receptionist?",
      a: "Any business that receives phone calls benefits, but the biggest impact goes to businesses that miss after-hours calls or cannot always answer during business hours. The most common industries using BizRnR include HVAC, dental practices, real estate, auto repair, restaurants, legal services, landscaping, and property management.",
    },
    {
      q: "How long does it take to set up an AI voice receptionist?",
      a: "Setup time depends on the call flow and connected services. BizRnR provides self-serve and managed setup paths for phone routing, scripts, business hours, intake questions, calendar access, and escalation. Test representative, unavailable-person, and failed-transfer calls before relying on the workflow.",
    },
    {
      q: "Do I need any technical knowledge to get started?",
      a: "No technical knowledge is required. The self-serve setup wizard takes most business owners just minutes to complete — pick your industry, connect your phone number, and start taking calls. For hands-off setup, our managed service handles phone integration, custom scripts, business hours, and industry configuration so you do not need to touch a thing.",
    },
    {
      q: "Can I cancel my AI voice receptionist subscription anytime?",
      a: "Yes, you can cancel your BizRnR subscription at any time from your dashboard with no penalties, no hidden fees, and no cancellation hoops. BizRnR has no long-term contracts — cancel whenever you want.",
    },
    {
      q: "Does the AI voice receptionist sound natural on the phone?",
      a: "BizRnR uses conversational voice synthesis designed for natural pacing and turn-taking. Voice quality is subjective and varies with the call, script, connection, and selected voice, so BizRnR does not claim that every caller will mistake it for a person. Use the live demo to evaluate the experience.",
    },
    {
      q: "How much does an AI receptionist cost?",
      a: "BizRnR starts at $99 per month with 100 interactions included and a published 30-day money-back guarantee; higher-volume and managed requirements use a custom path. Competitors may bill per call, per minute, by tier, or through custom pricing. Use current provider pricing pages and expected volume for a defensible comparison.",
    },
    {
      q: "What is the best AI receptionist for small business?",
      a: "The best AI receptionist depends on call volume, workflow, integrations, and whether human agents are required. BizRnR is a best fit for many small businesses that value a published $99 monthly starting price, configurable 24/7 inbound coverage, structured intake, appointment workflows, interaction records, and human escalation. BizRnR publishes its comparison methodology and sources.",
    },
    {
      q: "Can an AI receptionist replace a human receptionist?",
      a: "An AI receptionist can handle configured, repeatable tasks such as intake, FAQs, appointment requests, and routing. It should not replace human judgment for sensitive, urgent, regulated, or unfamiliar situations. BizRnR supports business-defined escalation so a person can take over when the workflow requires it.",
    },
    {
      q: "Does an AI receptionist work for HVAC companies?",
      a: "Yes. An HVAC business can configure BizRnR to collect equipment type, caller description, service address, availability, and firm-approved urgency categories. The business must define escalation rules and who is available after hours. BizRnR does not promise a universal return or make technical safety judgments independently.",
    },
    {
      q: "How does an AI receptionist book appointments?",
      a: "BizRnR's AI receptionist connects to your Google Calendar, Microsoft Outlook, or iCal and checks your real-time availability during the phone call. When a caller wants to book, the AI offers available time slots, confirms the appointment, and sends a confirmation SMS to the caller — all during the same phone call. You see the booking appear in your calendar instantly. No double-booking, no back-and-forth phone tag.",
    },
    {
      q: "Is BizRnR HIPAA conscious?",
      a: "BizRnR is designed with HIPAA-conscious practices for dental and healthcare clients — call recordings are encrypted, transcripts are stored securely, and access controls are enforced. However, BizRnR does not currently hold a formal HIPAA BAA (Business Associate Agreement). If your practice requires a signed BAA, contact our team to discuss your compliance requirements. For general dental appointment booking and patient screening, BizRnR's security posture meets the needs of most small practices.",
    },
  ],
  metadata: {
    title: "AI Voice Receptionist for Small Business (24/7 Calls) | BizRnR",
    description:
      "BizRnR's AI voice receptionist provides configurable 24/7 inbound coverage, structured intake, appointment workflows, and human escalation. Starts at $99/month with a 30-day money-back guarantee.",
    canonical: "https://bizrnr.com/ai-voice-receptionist",
  },
};
